import { useMutation } from "@tanstack/react-query";
import { Menu, Spin, Tooltip } from "antd";
import {
  FETCH_MODE,
  PERMISSIONS,
  RMPM_ROLE,
  ROLE,
  ROLE_CONFIG_KEYS
} from "common/constants";
import APIConstants from "common/constants/APIConstants";
import {
  allPaymentPageRoutes,
  getAllOrderManagementPageRoutes,
  getAllPurchaseOrderManagementPageRoutes
} from "common/constants/AllRoutes";
import COLORS from "common/constants/Colors";
import { useAuthContext } from "common/contexts/AuthContext";
import AllOrders from "common/icons/AllOrdersGreen.svg";
import BroadcastIcon from "common/icons/BroadcastGreen.svg";
import Configuration from "common/icons/ConfigurationGreen.svg";
import Coupon from "common/icons/CouponsGreen.svg";
import Customer from "common/icons/CustomerGreen.svg";
import Groups from "common/icons/GroupsGreen.svg";
import Inventory from "common/icons/InventoryGreen.svg";
import InvAnalyticsIcon from "common/icons/boxes.png";
import Invoice from "common/icons/InvoicesGreen.svg";
import LedgerGreen from "common/icons/LedgerGreen.svg";
import Logout from "common/icons/LogOutGreen.svg";
import LogisticsIcon from "common/icons/LogisticsIcon.svg";
import Offers from "common/icons/OffersGreen.svg";
import PaymentsIcon from "common/icons/PaymentsGreen.svg";
import TreasuryIcon from "common/icons/money-bag.png";
import PlaceOrder from "common/icons/PlusWhite.svg";
import Representative from "common/icons/RepresentativeGreen.svg";
import Subscription from "common/icons/SubscriptionsGreen.svg";
import Suppliers from "common/icons/SuppliersGreen.svg";
import Yard from "common/icons/block.png";
import BorrowRequest from "common/icons/cartGreen.svg";
import Cutter from "common/icons/cutter.png";
import Dashboard from "common/icons/dashboard2.png";
import History from "common/icons/historybox.png";
import GRN from "common/icons/receiver.png";
import reports from "common/icons/reports.svg";
import Sales from "common/icons/sales.png";
import SalesAnalyticsIcon from "common/icons/increase.png";
import Slabs from "common/icons/slabs.png";
import StoreInventory from "common/icons/store-inventory.png";
import SCM from "common/icons/supply-chain.png";
import ProductionCostImg from "common/icons/value-chain.png";
import valueMrpLogo from "common/icons/valueMrp_logo.png";
import Wiresaw from "common/icons/wiresaw.png";
import { checkPermission, getUserRole } from "common/utils";
import { api } from "common/utils/APIMethods";
import { EventType, ReactGATracker } from "common/utils/analyticsTracker";
import {
  PERMISSION_STRATEGIES,
  getPermissionStrategy
} from "common/utils/permissionStrategies";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  matchPath,
  useLocation,
  useMatch,
  useNavigate,
  useResolvedPath
} from "react-router-dom";
import styled, { css } from "styled-components";

const NavbarContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${COLORS.pale_cyan};
  color: ${COLORS.midnight_green};
  border-right: 1px solid rgba(0, 87, 84, 0.1);
  width: ${(props) => (props.open ? "180px" : "64px")};
  transition: width 0.3s ease;
  @media screen and (max-width: 1024px) {
    width: ${(props) => (props.open ? "150px" : "64px")};
  }
`;

const EnneaButton = styled.div`
  display: flex;
  justify-content: center;
  height: 7vh;
  border-bottom: 1px solid rgba(0, 87, 84, 0.1);
  cursor: pointer;
`;

const LinksContainer = styled.div`
  height: 95vh;
  overflow: auto;
  padding-bottom: 90px;
  ::-webkit-scrollbar {
    width: 0;
  }
`;

const NavElement = styled.div`
  padding: 10px 20px;
  border-bottom: 1px solid rgba(0, 87, 84, 0.1);
  ${(props) =>
    props.match &&
    css`
      color: white;
      background-color: ${COLORS.wood_rush};
    `}
`;

const Image = styled.img`
  width: 40%;
  height: 100%;
`;

const SmallImage = styled.img`
  width: 90%;
  height: 100%;
`;

const PlaceOrderElement = styled.div`
  width: 100%;
  height: 54px;
  display: flex;
  align-items: center;
  ${(props) => (props.match ? "padding-left: 0;" : "justify-content: center;")}
  background: ${(props) => (props.match ? COLORS.wood_rush : COLORS.pale_cyan)};
  border-bottom: 1px solid rgba(0, 87, 84, 0.1);
`;

const PlaceOrderLink = styled(Link)`
  display: flex;
  align-items: center;
  ${(props) =>
    !props.match
      ? `
      height: 38px;
      padding: 0 10px;
      border-radius: 4px;
      border: 1px solid rgba(0, 148, 143, 0.3);
      background: linear-gradient(315deg, #00bfb9 0%, rgba(0, 191, 185, 0.65) 100%);
      box-shadow: 0px 4px 6px -3px rgba(0, 191, 185, 0.6);
    `
      : `
      padding-left: 20px;
    `}
  cursor: pointer;
`;

const LinkElement = styled(Link)`
  display: flex;
  align-items: center;
  color: ${(props) => (props.match ? "white" : COLORS.midnight_green)};
  :hover {
    color: inherit;
  }
`;

const Texts = styled.div`
  padding-left: 10px;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  display: ${(props) => (props.open ? "flex" : "none")};
  white-space: nowrap;
  @media screen and (max-width: 1024px) {
    font-size: 10px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
`;
const TextsLogo = styled.div`
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  display: ${(props) => (props.open ? "flex" : "none")};
  white-space: nowrap;
  @media screen and (max-width: 1024px) {
    font-size: 10px;
  }
`;

const PlaceOrderText = styled.div`
  display: ${(props) => (props.open ? "flex" : "none")};
  padding-left: 14px;
  color: #fff;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
`;

const LogoutLogo = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  background-color: ${COLORS.pale_cyan};
  padding: 0 20px;
  height: 60px;
  border-top: 1px solid rgba(0, 87, 84, 0.1);
  border-right: 1px solid rgba(0, 87, 84, 0.1);
  cursor: pointer;
  position: fixed;
  bottom: 0;
  width: ${(props) => (props.open ? "180px" : "64px")};
  @media screen and (max-width: 1024px) {
    width: ${(props) => (props.open ? "150px" : "64px")};
  }
`;

const LogoutText = styled.div`
  color: ${COLORS.midnight_green};
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  padding-left: 10px;
  display: ${(props) => (props.open ? "flex" : "none")};
  @media screen and (max-width: 1024px) {
    font-size: 10px;
  }
`;

const CustomMenu = styled(Menu)`
  border-bottom: 1px solid rgba(0, 87, 84, 0.1);
  .ant-menu-submenu-arrow {
    display: none;
  }
  .ant-menu-submenu > .ant-menu-submenu-title {
    padding: 0;
    margin: 0;
  }
  height: ${(props) => (props.match ? "44px" : "60px")};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CustomItem = styled(Menu.Item)`
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid rgba(0, 87, 84, 0.1) !important;
  padding: 0 !important;
  margin: 0 !important;
  height: 44px !important;
  .ant-menu-title-content > a {
    color: white !important;
  }
`;

const MenuTitleContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.match ? `${COLORS.wood_rush}` : `${COLORS.pale_cyan}`};
  width: 100%;
  height: 44px;
  ${(props) =>
    !props.match &&
    `
    width: 80%;
    height: 80%;
    padding: 10px 20px;
    border: 1px solid rgba(0, 148, 143, 0.3);
    border-radius: 4px;
    background: linear-gradient(275deg, #00bfb9 0%, rgba(0, 191, 185, 0.65) 100%);
    box-shadow: 0px 4px 6px -3px rgba(0, 191, 185, 0.6);
    `}
`;

const SubMenuText = styled.div`
  padding-left: 12px;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${(props) => (props.match ? "white" : COLORS.midnight_green)};
  display: ${(props) => (props.open ? "flex" : "none")};
`;

const MenuItemLink = styled(Link)`
  display: flex;
  width: 140px;
  column-gap: 12px;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  ${(props) =>
    props.match
      ? `
    background-color: ${COLORS.wood_rush};
    padding: 10px;
  `
      : `
  padding: 8px;
  border: 1px solid rgba(0, 148, 143, 0.3);
  border-radius: 4px;
  background: linear-gradient(275deg, #00bfb9 0%, rgba(0, 191, 185, 0.65) 100%);
  box-shadow: 0px 4px 6px -3px rgba(0, 191, 185, 0.6);
  `}
`;

const NavbarLink = ({
  open,
  to: forwardTo,
  image,
  text,
  parent = forwardTo,
  matchPaths = null,
  gaValue
}) => {
  const location = useLocation();
  const resolved = useResolvedPath(parent);
  const defaultMatch = useMatch({ path: resolved.pathname, end: false });
  const customMatch =
    matchPaths?.some((item) =>
      matchPath({ path: item.path, end: item.end ?? false }, location.pathname)
    ) ?? false;
  const match = matchPaths ? customMatch : defaultMatch;
  const { t } = useTranslation();

  if (parent === "/placeorder") {
    return (
      <Tooltip
        title={!open ? t(text) : ""}
        trigger={["hover", "focus"]}
        placement="right"
      >
        <PlaceOrderElement open={open} match={match}>
          <PlaceOrderLink
            match={match}
            open={open}
            to={forwardTo}
            onClick={() => {
              ReactGATracker(gaValue);
            }}
          >
            <img height={20} width={20} src={PlaceOrder} alt="placeOrder" />
            <PlaceOrderText match={match} open={open}>
              {t(text)}
            </PlaceOrderText>
          </PlaceOrderLink>
        </PlaceOrderElement>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={!open ? t(text) : ""}
      trigger={["hover", "focus"]}
      placement="right"
    >
      <NavElement match={match}>
        <LinkElement
          open={open}
          match={match}
          to={forwardTo}
          onClick={() => {
            ReactGATracker(gaValue);
          }}
        >
          <img src={image} height={24} width={24} alt="logo" />
          <Texts open={open}>{t(text)}</Texts>
        </LinkElement>
      </NavElement>
    </Tooltip>
  );
};

export const checkPermissionForNavItem = (
  permission,
  strategy = "simple",
  parsedRoleConfig = undefined,
  userRole = ""
) => {
  const checkIfAuthorized = getPermissionStrategy(strategy);

  if (
    strategy === PERMISSION_STRATEGIES.placeOrder &&
    userRole === ROLE.salesman
  ) {
    return false;
  }
  return checkIfAuthorized(parsedRoleConfig, permission);
};

export const navBarConfig = [
  {
    component: {
      keyItem: "Master Reports",
      to: "/masterReports",
      text: "Reports",
      image: reports
    },
    permission: {
      config: ROLE_CONFIG_KEYS.masterReports,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "placeOrder",
      parent: "/placeorder",
      to: "/placeorder/new",
      text: "place_order",
      image: PlaceOrder,
      gaValue: EventType.NAVBAR_ON_PLACE_ORDER
    },
    permission: {
      config: ROLE_CONFIG_KEYS.cart,
      permission: PERMISSIONS.create
    },
    strategy: PERMISSION_STRATEGIES.placeOrder
  },
  // granite
  {
    component: {
      keyItem: "dashboard",
      to: "/dashboard",
      text: "dashboard",
      image: Dashboard,
      gaValue: EventType.NAVBAR_ON_DASHBOARD
    },
    permission: {
      config: ROLE_CONFIG_KEYS.graniteAnalytics,
      permission: PERMISSIONS.read
    },
    graniteKey: "graniteAnalytics"
  },
  // rmpm
  {
    component: {
      keyItem: "orders",
      parent: "/orders",
      to: "/orders/list/default",
      text: "MRI",
      image: AllOrders,
      gaValue: EventType.NAVBAR_ON_ALL_ORDERS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.order,
      permission: PERMISSIONS.read
    },
    allRoutes: getAllOrderManagementPageRoutes("")
  },
  // granite
  {
    component: {
      keyItem: "yard",
      to: "/yard",
      text: "Yard",
      image: Yard,
      gaValue: EventType.NAVBAR_ON_YARD
    },
    permission: {
      config: ROLE_CONFIG_KEYS.yard,
      permission: PERMISSIONS.read
    },
    graniteKey: "yard"
  },
  // granite
  {
    component: {
      keyItem: "wiresaw",
      to: "/wiresaw",
      text: "Wiresaw",
      image: Wiresaw,
      gaValue: EventType.NAVBAR_ON_WIRESAW
    },
    permission: {
      config: ROLE_CONFIG_KEYS.wiresaw,
      permission: PERMISSIONS.read
    },
    graniteKey: "wireSaw"
  },
  // granite
  {
    component: {
      keyItem: "cutter",
      to: "/cutter",
      text: "Cutter",
      image: Cutter,
      gaValue: EventType.NAVBAR_ON_CUTTER
    },
    permission: {
      config: ROLE_CONFIG_KEYS.Cutter,
      permission: PERMISSIONS.read
    },
    graniteKey: "cutter"
  },
  // granite
  {
    component: {
      keyItem: "sub-blocks",
      to: "/slab-details",
      text: "Slab Details",
      image: Slabs
    },
    permission: {
      config: ROLE_CONFIG_KEYS.Slab,
      permission: PERMISSIONS.read
    },
    graniteKey: "slab"
  },
  {
    component: {
      keyItem: "drugInfo",
      parent: "/drugInfo",
      to: "/drugInfo",
      text: "drug_info",
      image: Inventory,
      gaValue: EventType.NAVBAR_ON_DRUG_INFO
    },
    permission: {
      config: ROLE_CONFIG_KEYS.product,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "broadcasts",
      parent: "/broadcasts",
      to: "/broadcasts",
      text: "broadcasts",
      image: BroadcastIcon,
      gaValue: EventType.NAVBAR_ON_BROADCAST
    },
    permission: {
      config: ROLE_CONFIG_KEYS.broadcasts,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "payments",
      parent: "/payments",
      to: "/payments/outstanding",
      text: "payments",
      image: PaymentsIcon,
      gaValue: EventType.NAVBAR_ON_PAYMENTS
    },
    permission: [
      {
        config: ROLE_CONFIG_KEYS.payment,
        permission: PERMISSIONS.read
      },
      {
        config: ROLE_CONFIG_KEYS.outstanding,
        permission: PERMISSIONS.read
      }
    ],
    strategy: PERMISSION_STRATEGIES.atLeastOneTrue,
    allRoutes: allPaymentPageRoutes
  },
  {
    component: {
      keyItem: "quotations",
      parent: "/quotations",
      to: "/quotations",
      text: "flash_sale",
      image: PaymentsIcon,
      gaValue: EventType.NAVBAR_ON_QUOTATIONS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.quotations,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "invoices",
      parent: "/invoices",
      to: "/invoices/list",
      text: "invoices",
      image: Invoice,
      gaValue: EventType.NAVBAR_ON_INVOICES
    },
    permission: {
      config: ROLE_CONFIG_KEYS.invoices,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "suppliers",
      parent: "/suppliers",
      to: "/suppliers",
      text: "suppliers",
      image: Suppliers,
      gaValue: EventType.NAVBAR_ON_SUPPLIERS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.suppliers,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "Groups",
      parent: "/groups",
      to: "/groups",
      text: "groups",
      image: Groups,
      gaValue: EventType.NAVBAR_ON_GROUPS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.groups,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "customers",
      parent: "/customers",
      to: "/customers",
      text: "customers",
      image: Customer,
      gaValue: EventType.NAVBAR_ON_CUSTOMERS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.customers,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "ledgers",
      parent: "/ledgers",
      to: "/ledgers",
      text: "Ledgers",
      image: LedgerGreen,
      gaValue: EventType.NAVBAR_ON_LEDGER
    },
    permission: {
      config: ROLE_CONFIG_KEYS.customers,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "representatives",
      parent: "/representatives",
      to: "/representatives",
      text: "representatives",
      image: Representative,
      gaValue: EventType.NAVBAR_ON_REPRESENTATIVES
    },
    permission: {
      config: ROLE_CONFIG_KEYS.representatives,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "logistics",
      parent: "/logistics",
      to: "/logistics",
      text: "Logistics",
      image: LogisticsIcon,
      gaValue: EventType.NAVBAR_ON_LOGISTICS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.logistics,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "coupons",
      parent: "/coupons",
      to: "/coupons",
      text: "coupons",
      image: Coupon,
      gaValue: EventType.NAVBAR_ON_COUPONS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.coupons,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "offers",
      parent: "/offers",
      to: "/offers",
      text: "offers",
      image: Offers,
      gaValue: EventType.NAVBAR_ON_OFFERS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.specialOffers,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "subscriptions",
      parent: "/subscriptions",
      to: "/subscriptions",
      text: "Subscriptions",
      image: Subscription,
      gaValue: EventType.NAVBAR_ON_SUBSCRIPTIONS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.subscriptions,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "erp-dashboard",
      to: "/erp-dashboard",
      text: "dashboard",
      image: Dashboard
    },
    permission: {
      config: ROLE_CONFIG_KEYS.Erp_Dashboard,
      permission: PERMISSIONS.read
    }
  },
  // granite
  {
    component: {
      keyItem: "history",
      to: "/history",
      text: "History",
      image: History
    },
    permission: {
      config: ROLE_CONFIG_KEYS.History,
      permission: PERMISSIONS.read
    },
    graniteKey: "history"
  },
  // granite
  {
    component: {
      keyItem: "sale",
      parent: "/sale",
      to: "/sale/list",
      text: "Sales",
      image: Sales,
      gaValue: EventType.NAVBAR_ON_SALE
    },
    permission: {
      config: ROLE_CONFIG_KEYS.sale,
      permission: PERMISSIONS.read
    },
    graniteKey: "sale"
  },
  // granite
  {
    component: {
      keyItem: "storeInventory",
      parent: "/storeInventory",
      to: "/storeInventory",
      text: "Store Inventory",
      image: StoreInventory,
      gaValue: EventType.NAVBAR_ON_SALE
    },
    permission: {
      config: ROLE_CONFIG_KEYS.graniteStoreInventory,
      permission: PERMISSIONS.read
    },
    graniteKey: "storeInventory"
  },
  // granite
  {
    component: {
      keyItem: "Production Cost",
      parent: "/productionCost",
      to: "/productionCost",
      text: "Production Cost",
      image: ProductionCostImg,
      gaValue: EventType.NAVBAR_ON_SALE
    },
    permission: {
      config: ROLE_CONFIG_KEYS.graniteCostAnalysis,
      permission: PERMISSIONS.read
    },
    graniteKey: "costAnalysis"
  },
  // granite
  {
    component: {
      keyItem: "settings",
      parent: "/settings",
      to: "/settings",
      text: "Settings",
      image: Configuration,
      gaValue: EventType.NAVBAR_ON_SETTINGS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.graniteSettings,
      permission: PERMISSIONS.read
    },
    graniteKey: "userSettings"
  },

  // RMPM Features

  {
    component: {
      keyItem: "purchaseorders",
      parent: "/purchase-order",
      to: "/purchase-order/list/default",
      text: "Purchase Orders",
      image: AllOrders,
      gaValue: EventType.NAVBAR_ON_ALL_PURCHASE_ORDERS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.suppliers,
      permission: PERMISSIONS.read
    },
    allRoutes: getAllPurchaseOrderManagementPageRoutes("")
  },
  // rmpm
  {
    component: {
      keyItem: "goods",
      parent: "/goods",
      to: "/goods",
      text: "Goods",
      image: AllOrders
    },
    permission: {
      config: ROLE_CONFIG_KEYS.goods,
      permission: PERMISSIONS.read
    },
    allRoutes: getAllOrderManagementPageRoutes("")
  },
  // rmpm
  {
    component: {
      keyItem: "grn",
      parent: "/grn",
      to: "/grn",
      text: "GRN",
      image: GRN,
      gaValue: EventType.NAVBAR_ON_INVENTORY
    },
    permission: {
      config: ROLE_CONFIG_KEYS.grn,
      permission: PERMISSIONS.read
    }
  },
  // rmpm
  {
    component: {
      keyItem: "inventory",
      parent: "/inventory",
      to: "/inventory",
      text: "Inventory",
      image: Inventory,
      gaValue: EventType.NAVBAR_ON_INVENTORY
    },
    permission: {
      config: ROLE_CONFIG_KEYS.inventory,
      permission: PERMISSIONS.read
    }
  },

  {
    component: {
      keyItem: "AllUsers",
      to: "/all-users",
      text: "All Users",
      image: Customer
    },
    permission: {
      config: ROLE_CONFIG_KEYS.Erp_Users,
      permission: PERMISSIONS.update
    }
  },
  {
    component: {
      keyItem: "CreateUser",
      to: "/create-user",
      text: "Users",
      image: Customer
    },
    permission: {
      config: ROLE_CONFIG_KEYS.createUser,
      permission: PERMISSIONS.create
    }
  },
  // rmpm
  {
    component: {
      keyItem: "product-inventory",
      to: "/product-inventory",
      text: "Product Inventory",
      image: Inventory
    },
    permission: {
      config: ROLE_CONFIG_KEYS.productInfo,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "scm",
      to: "/scm",
      matchPaths: [
        { path: "/scm", end: true },
        { path: "/scm/manage", end: false }
      ],
      text: "SCM",
      image: SCM
    },
    permission: {
      config: ROLE_CONFIG_KEYS.scm,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "scmProduct",
      to: "/scm/pm",
      matchPaths: [{ path: "/scm/pm", end: false }],
      text: "SCM Product",
      image: Inventory
    },
    permission: {
      config: ROLE_CONFIG_KEYS.scmProduct,
      permission: PERMISSIONS.update
    }
  },
  // rmpm
  {
    component: {
      keyItem: "products",
      to: "/pm-order",
      text: "Products",
      image: Inventory
    },
    permission: {
      config: ROLE_CONFIG_KEYS.order,
      permission: PERMISSIONS.create
    }
  },
  {
    component: {
      keyItem: "products",
      to: "/product-mapping",
      text: "Product Mapping",
      image: StoreInventory
    },
    permission: {
      config: ROLE_CONFIG_KEYS.productMapping,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "rmpmSale",
      to: "/sales/saleList",
      text: "Sales",
      image: PaymentsIcon
    },
    permission: {
      config: ROLE_CONFIG_KEYS.rmpmSale,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "din",
      parent: "/din",
      to: "/din",
      text: "DIN",
      image: Inventory,
      gaValue: EventType.NAVBAR_ON_DASHBOARD
    },
    permission: {
      config: ROLE_CONFIG_KEYS.din,
      permission: PERMISSIONS.create
    }
  },
  {
    component: {
      keyItem: "saleQuotation",
      to: "/sale-quotation",
      text: "Quotation",
      image: LedgerGreen
    },
    permission: {
      config: ROLE_CONFIG_KEYS.rmpmSaleQuotation,
      permission: PERMISSIONS.read
    }
  },
  // reports
  {
    component: {
      keyItem: "reports",
      parent: "/reports",
      to: "/reports",
      text: "Reports",
      image: reports,
      gaValue: EventType.NAVBAR_ON_REPORTS
    },
    permission: {
      config: ROLE_CONFIG_KEYS.reports,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "purchaseOrder",
      to: "/scm-po",
      text: "Purchase Order",
      image: LedgerGreen
    },
    permission: {
      config: ROLE_CONFIG_KEYS.scmPo,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "dinData",
      to: "/dinData",
      text: "Din Data",
      image: Inventory,
      gaValue: EventType.NAVBAR_ON_DASHBOARD
    },
    permission: {
      config: ROLE_CONFIG_KEYS.dinData,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "grnQc",
      parent: "/grn-qc",
      to: "/grn-qc",
      text: "GRN QC",
      image: Inventory
    },
    permission: {
      config: ROLE_CONFIG_KEYS.grnQc,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "ledger",
      parent: "/ledger",
      to: "/ledger",
      text: "Ledger",
      image: Inventory
    },
    permission: {
      config: ROLE_CONFIG_KEYS.ledger,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "Reservation",
      to: "/reservation",
      text: "Reservation",
      image: LedgerGreen
    },
    permission: {
      config: ROLE_CONFIG_KEYS.reservation,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "Borrow Request",
      to: "/borrow-requests",
      text: "Borrow Request",
      image: BorrowRequest
    },
    permission: {
      config: ROLE_CONFIG_KEYS.borrowRequest,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "salesReports",
      parent: "/sales-reports",
      to: "/sales-reports/quotationdashboard",
      text: "Sales Reports",
      image: Sales,
      gaValue: EventType.NAVBAR_ON_SALE
    },
    permission: {
      config: ROLE_CONFIG_KEYS.salesReports,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "pharmaSettings",
      to: "/pharma-settings",
      text: "Settings",
      image: Configuration,
      gaValue: EventType.NAVBAR_ON_SALE
    },
    permission: {
      config: ROLE_CONFIG_KEYS.settings,
      permission: PERMISSIONS.update
    }
  },

  {
    component: {
      keyItem: "Finance",
      to: "/finance-analytics",
      text: "Treasury Analytics",
      image: TreasuryIcon
    },
    permission: {
      config: ROLE_CONFIG_KEYS.treasuryAnalytics,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "SaleAnalytics",
      to: "/sales-analytics",
      text: "Sales Analytics",
      image: SalesAnalyticsIcon
    },
    permission: {
      config: ROLE_CONFIG_KEYS.saleAnalytics,
      permission: PERMISSIONS.read
    }
  },
  {
    component: {
      keyItem: "InventoryAnalytics",
      to: "/inventory-analytics",
      text: "Inventory Analytics",
      image: InvAnalyticsIcon
    },
    permission: {
      config: ROLE_CONFIG_KEYS.inventoryAnalytics,
      permission: PERMISSIONS.read
    }
  }
];

const Navbar = () => {
  const navigate = useNavigate();
  const { dispatch, parsedRoleConfig, authConfig } = useAuthContext();
  const userRole = getUserRole(authConfig);
  const isRepresentative = userRole === ROLE.salesman;
  const isRoleRetailer =
    userRole === ROLE.retailer || userRole === ROLE.retailerSingleDistributor;
  const isRoleDistributor = userRole === ROLE.distributor;
  const isSuperAdmin = userRole === RMPM_ROLE.superAdmin;
  const isFetchModeCustomer = authConfig?.fetchMode === FETCH_MODE.customer;
  const showLedgerOrder =
    isFetchModeCustomer && (isRoleDistributor || isRepresentative);
  const [navbarOpen, setNavbarOpen] = useState(() => window.innerWidth >= 1024);
  const { t } = useTranslation();

  const isRoleStoreAdmin = userRole === RMPM_ROLE.storeAdmin;
  const isRoleStoreLocationHead = userRole === RMPM_ROLE.storeLocationHead;
  const isRoleStoreHead = userRole === RMPM_ROLE.storeHead;
  const isRoleSalesHead = userRole === RMPM_ROLE.salesHead;

  useEffect(() => {
    const handleResize = () => {
      setNavbarOpen(window.innerWidth >= 1024);
    };
    if (navbarOpen) {
      window.addEventListener("resize", handleResize);
    }
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const logoutMutation = useMutation({
    mutationFn: () =>
      api(
        {
          url: APIConstants.logoutURL
        },
        authConfig
      ),
    onSuccess: () => {
      dispatch({ type: "onLogout" });
      navigate("/login");
      window.location.reload();
      localStorage.clear();
    }
  });
  const graniteSettings = authConfig?.user?.graniteSettings || {};

  const authorizedNavBarElements = useMemo(
    () =>
      navBarConfig
        .filter((navItem) =>
          navItem.graniteKey
            ? graniteSettings[navItem.graniteKey] === true
            : true
        )
        .filter(
          (navItem) =>
            !(isSuperAdmin && navItem.component.keyItem === "salesReports")
        )
        .filter((navItem) =>
          checkPermissionForNavItem(
            navItem.permission,
            navItem.strategy,
            parsedRoleConfig,
            userRole
          )
        )
        .map((item) => {
          if (item.component.keyItem === "orders") {
            return {
              ...item,
              component: {
                ...item.component,
                text:
                  isRoleStoreAdmin || isRoleStoreHead || isRoleStoreLocationHead
                    ? "MRI"
                    : "MRD"
              }
            };
          }
          return item;
        })
        .map((navItem) => {
          if (navItem.strategy === PERMISSION_STRATEGIES.atLeastOneTrue) {
            const forwardTo = navItem.allRoutes?.find((route) =>
              checkPermission(parsedRoleConfig, route.permission)
            )?.to;

            if (!forwardTo) return null;

            return {
              component: { ...navItem.component, to: forwardTo }
            };
          }
          return navItem;
        })
        .filter(
          (navItem) =>
            !(
              isRoleSalesHead &&
              navItem.component.keyItem === "product-inventory"
            )
        )
        .filter(Boolean),
    [
      graniteSettings,
      isSuperAdmin,
      parsedRoleConfig,
      userRole,
      isRoleStoreAdmin,
      isRoleStoreHead,
      isRoleStoreLocationHead,
      isRoleSalesHead
    ]
  );

  const onClickLogout = () => {
    logoutMutation.mutate();
    ReactGATracker(EventType.NAVBAR_ON_LOGOUT);
  };

  const toggleNavbar = () => {
    if (navbarOpen) {
      ReactGATracker(EventType.NAVBAR_ON_CLOSE);
    } else {
      ReactGATracker(EventType.NAVBAR_ON_OPEN);
    }
    setNavbarOpen(!navbarOpen);
  };

  return (
    <NavbarContainer open={navbarOpen}>
      <EnneaButton onClick={toggleNavbar}>
        {navbarOpen ? (
          <LogoContainer>
            <Image src={valueMrpLogo} alt="logo" />
            <TextsLogo open="true">ValueMrp</TextsLogo>
          </LogoContainer>
        ) : (
          <SmallImage src={valueMrpLogo} alt="logo" />
        )}
      </EnneaButton>
      <LinksContainer>
        {authorizedNavBarElements.map((navItem) => (
          <NavbarLink
            open={navbarOpen}
            key={navItem.component.keyItem + navItem.component.to}
            {...navItem.component}
          />
        ))}
        {showLedgerOrder && (
          <Tooltip title={`${t("ledger_order")}`} trigger={["hover", "focus"]}>
            <NavbarLink
              open={navbarOpen}
              key="ledgerOrder"
              to="/ledgerOrder"
              text={`${t("ledger_order")}`}
              image={AllOrders}
              gaValue={EventType.NAVBAR_ON_LEDGER_ORDER}
            />
          </Tooltip>
        )}
        {isRoleRetailer && (
          <NavbarLink
            open={navbarOpen}
            key="credit"
            to="/credit"
            text="Credit"
            image={PaymentsIcon}
            gaValue={EventType.NAVBAR_ON_CREDIT}
          />
        )}
        <Tooltip
          title={!navbarOpen ? t("logout") : ""}
          trigger={["hover", "focus"]}
        >
          <LogoutLogo open={navbarOpen} onClick={onClickLogout}>
            {logoutMutation.isPending ? (
              <Spin />
            ) : (
              <img src={Logout} alt="logo" />
            )}
            <LogoutText open={navbarOpen}>{t("logout")}</LogoutText>
          </LogoutLogo>
        </Tooltip>
      </LinksContainer>
    </NavbarContainer>
  );
};

export default React.memo(Navbar);
